import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Heading,
  VStack,
  FormControl,
  FormLabel,
  Select,
  Switch,
  Button,
  useToast,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Input,
  IconButton,
  HStack,
  Badge,
  Text,
} from "@chakra-ui/react";
import { AddIcon, DeleteIcon } from "@chakra-ui/icons";
import { gql, useQuery, useMutation, useApolloClient, ApolloQueryResult } from "@apollo/client";
import { useNavigate } from "react-router-dom";
import { NavbarWithCallToAction } from "../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";

const GET_ALL_PROJECTS = gql`
  query GetAllProjects {
    projects {
      id
      projectName
      projectGoal
      progress
      billingClient {
        id
        fName
        lName
        email
        businessName
      }
      members {
        id
        fName
        lName
        email
      }
      tasks {
        id
        description
        status
        order
        billed
        assignedTo {
          id
          fName
          lName
        }
        media {
          url
          description
          fileType
        }
      }
      bills {
        id
        lineItems {
          id
          amount
        }
        isPaid
        status
      }
    }
  }
`;

const GET_PROJECT_WITH_TASKS = gql`
  query GetProjectWithTasks($projectId: ID!) {
    project(id: $projectId) {
      id
      projectName
      projectGoal
      progress
      billingClient {
        id
        fName
        lName
        email
        businessName
      }
      tasks {
        id
        description
        status
        order
        billed
        assignedTo {
          id
          fName
          lName
        }
        media {
          url
          description
          fileType
        }
      }
    }
  }
`;

const CREATE_BILL = gql`
  mutation CreateBill($input: BillInput!) {
    createBill(input: $input) {
      id
      isPaid
      paymentMethod
      lineItems {
        id
        amount
        description
      }
      projectId
      createdAt
      updatedAt
    }
  }
`;

interface LineItem {
  description: string;
  amount: number;
  taskId?: string; // Optional taskId to track which task this line item is related to
  status?: string; // Optional task status
}

const NewBill = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const client = useApolloClient(); // Get Apollo client instance
  
  const [selectedProject, setSelectedProject] = useState("");
  const [isPaid, setIsPaid] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<"AUD_TRANSFER" | "CRYPTO" | "STRIPE">("AUD_TRANSFER");
  const [lineItems, setLineItems] = useState<LineItem[]>([{ description: "", amount: 0 }]);

  const { loading: projectsLoading, error: projectsError, data: projectsData } = useQuery(GET_ALL_PROJECTS, {
    onCompleted: (data) => {
      console.log("Project data received from query:", data);
      if (data?.projects) {
        console.log(`Received ${data.projects.length} projects`);
        // Check the structure of the first project if available
        if (data.projects.length > 0) {
          const sampleProject = data.projects[0];
          console.log("Sample project structure:", {
            id: sampleProject.id,
            name: sampleProject.projectName,
            tasksCount: sampleProject.tasks?.length || 0,
            hasTasks: Array.isArray(sampleProject.tasks),
            billsCount: sampleProject.bills?.length || 0,
            firstTaskBilled: sampleProject.tasks && sampleProject.tasks.length > 0 
              ? sampleProject.tasks[0].billed 
              : "No tasks"
          });
        }
      } else {
        console.log("No projects data received");
      }
    },
    onError: (error) => {
      console.error("Error fetching projects:", error);
    }
  });
  const [createBill, { loading: creatingBill }] = useMutation(CREATE_BILL);

  // Pre-populate line items when a project is selected
  const handleProjectSelect = (projectId: string) => {
    console.log("Project selected:", projectId);
    setSelectedProject(projectId);
    
    if (!projectId) {
      console.log("No project selected, resetting line items");
      setLineItems([{ description: "", amount: 0 }]);
      return;
    }
    
    // Set a loading line item while we fetch data
    setLineItems([{ description: "Loading tasks...", amount: 0 }]);
    
    // Fetch the specific project with its tasks
    client.query<{ project: any }>({
      query: GET_PROJECT_WITH_TASKS,
      variables: { projectId },
      fetchPolicy: "network-only" // Force a fresh fetch
    }).then((response: ApolloQueryResult<{ project: any }>) => {
      console.log("Project details fetched:", response.data);
      
      const projectData = response.data.project;
      if (!projectData) {
        console.log("Project data not found");
        setLineItems([{ description: "", amount: 0 }]);
        toast({
          title: "Error",
          description: "Could not load project details",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
        return;
      }
      
      console.log("Project tasks:", projectData.tasks);
      console.log("Task count:", projectData.tasks?.length || 0);
      
      // Check if tasks exist
      if (!projectData.tasks || projectData.tasks.length === 0) {
        console.log("No tasks found in this project");
        setLineItems([{ description: "", amount: 0 }]);
        toast({
          title: "No tasks",
          description: "This project has no tasks. You can add custom line items.",
          status: "info",
          duration: 3000,
          isClosable: true,
        });
        return;
      }
      
      // Get tasks that aren't already billed
      const unbilledTasks = projectData.tasks.filter((task: any) => {
        console.log(`Task ${task.id} - ${task.description} - Billed:`, task.billed);
        // We need to check explicitly for false since billed is a boolean
        return task.billed === false;
      });
      
      console.log("Unbilled tasks:", unbilledTasks);
      console.log("Unbilled task count:", unbilledTasks.length);
      
      if (unbilledTasks.length === 0) {
        // If no unbilled tasks, keep an empty line item
        console.log("No unbilled tasks found");
        setLineItems([{ description: "", amount: 0 }]);
        toast({
          title: "No unbilled tasks",
          description: "This project has no unbilled tasks. You can add custom line items.",
          status: "info",
          duration: 3000,
          isClosable: true,
        });
        return;
      }
      
      // Create line items from unbilled tasks
      const newLineItems = unbilledTasks.map((task: any) => {
        console.log(`Creating line item for task: ${task.description}`);
        return {
          description: task.description,
          amount: 0, // Default to 0, user will need to set amount
          taskId: task.id, // Store the task ID for reference
          status: task.status // Store the task status for reference
        };
      });
      
      console.log("New line items:", newLineItems);
      setLineItems(newLineItems);
      
      toast({
        title: "Tasks loaded",
        description: `Loaded ${newLineItems.length} unbilled tasks as line items.`,
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    }).catch(error => {
      console.error("Error fetching project details:", error);
      setLineItems([{ description: "", amount: 0 }]);
      toast({
        title: "Error",
        description: "Failed to load project tasks. Please try again.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    });
  };

  const handleAddLineItem = () => {
    setLineItems([...lineItems, { description: "", amount: 0 }]);
  };

  const handleRemoveLineItem = (index: number) => {
    setLineItems(lineItems.filter((_, i) => i !== index));
  };

  const handleLineItemChange = (index: number, field: keyof LineItem, value: string | number) => {
    const newLineItems = [...lineItems];
    newLineItems[index] = {
      ...newLineItems[index],
      [field]: field === "amount" ? parseFloat(value as string) || 0 : value,
    };
    setLineItems(newLineItems);
  };

  const handleSubmit = async () => {
    if (!selectedProject) {
      toast({
        title: "Error",
        description: "Please select a project",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // Filter out line items with no description or zero amount
    const validLineItems = lineItems.filter(item => item.description && item.amount > 0);
    
    if (validLineItems.length === 0) {
      toast({
        title: "Error",
        description: "Please add at least one line item with a description and amount",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      // Remove taskId and status from lineItems before submitting
      const cleanedLineItems = validLineItems.map(({ description, amount }) => ({
        description,
        amount
      }));
      
      const { data } = await createBill({
        variables: {
          input: {
            projectId: selectedProject,
            isPaid,
            paymentMethod,
            lineItems: cleanedLineItems,
          },
        },
      });

      toast({
        title: "Success",
        description: "Bill created successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      navigate(`/bill/${data.createBill.id}`);
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to create bill",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  return (
    <>
      <NavbarWithCallToAction />
      <Container maxW="container.xl" py={8}>
        <VStack spacing={8} align="stretch">
          <Heading>Create New Bill</Heading>

          <FormControl>
            <FormLabel>Project</FormLabel>
            <Select
              placeholder="Select project"
              value={selectedProject}
              onChange={(e) => handleProjectSelect(e.target.value)}
              isDisabled={projectsLoading}
            >
              {projectsData?.projects.map((project: any) => (
                <option key={project.id} value={project.id}>
                  {project.projectName} - {project.billingClient?.businessName || `${project.billingClient?.fName} ${project.billingClient?.lName}`}
                </option>
              ))}
            </Select>
          </FormControl>

          <FormControl>
            <FormLabel>Payment Method</FormLabel>
            <Select
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(e.target.value as any)}
            >
              <option value="AUD_TRANSFER">AUD Transfer</option>
              <option value="CRYPTO">Crypto</option>
              <option value="STRIPE">Stripe</option>
            </Select>
          </FormControl>

          <FormControl display="flex" alignItems="center">
            <FormLabel mb="0">Is Paid?</FormLabel>
            <Switch isChecked={isPaid} onChange={(e) => setIsPaid(e.target.checked)} />
          </FormControl>

          <Box>
            <HStack mb={4} justify="space-between">
              <Heading size="md">Line Items</Heading>
              {selectedProject && (
                <Text fontSize="sm" color="gray.600">
                  Line items are pre-populated with unbilled tasks from the selected project
                </Text>
              )}
            </HStack>
            <Table variant="simple">
              <Thead>
                <Tr>
                  <Th>Description</Th>
                  <Th>Amount</Th>
                  <Th>Status</Th>
                  <Th>Action</Th>
                </Tr>
              </Thead>
              <Tbody>
                {lineItems.map((item, index) => (
                  <Tr key={index}>
                    <Td>
                      <Input
                        value={item.description}
                        onChange={(e) => handleLineItemChange(index, "description", e.target.value)}
                        placeholder="Item description"
                      />
                    </Td>
                    <Td>
                      <Input
                        type="number"
                        value={item.amount}
                        onChange={(e) => handleLineItemChange(index, "amount", e.target.value)}
                        placeholder="Amount"
                      />
                    </Td>
                    <Td>
                      {item.status && (
                        <Badge colorScheme={
                          item.status === "COMPLETED" ? "green" :
                          item.status === "IN_PROGRESS" ? "yellow" : "gray"
                        }>
                          {item.status}
                        </Badge>
                      )}
                    </Td>
                    <Td>
                      <IconButton
                        aria-label="Remove line item"
                        icon={<DeleteIcon />}
                        onClick={() => handleRemoveLineItem(index)}
                        isDisabled={lineItems.length === 1}
                      />
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
            <Button
              leftIcon={<AddIcon />}
              onClick={handleAddLineItem}
              mt={4}
              size="sm"
            >
              Add Line Item
            </Button>
          </Box>

          <HStack spacing={4}>
            <Button
              colorScheme="blue"
              onClick={handleSubmit}
              isLoading={creatingBill}
            >
              Create Bill
            </Button>
            <Button onClick={() => navigate("/bills")}>
              Cancel
            </Button>
          </HStack>
        </VStack>
      </Container>
      <FooterWithFourColumns />
    </>
  );
};

export default NewBill; 